import { createMachine } from 'xstate';
import { pure } from 'xstate/lib/actions';

import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'common/bento/types/Abstract';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';

import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import { Context } from './model';
import services, { Service } from './services';

export enum State {
  CREATION_PRESENTATION = 'creation-presentation',
  PLAN_SELECTION = 'plan-selection',
  PACKAGE_RECAP = 'package-recap',
  TERMS_AND_CONDITIONS = 'terms-and-conditions',

  PACKAGE_PAYMENT = 'package-payment',
  PACKAGE_PAYMENT_SEND = 'package-payment-send',
  PACKAGE_PAYMENT_SUCCESSFUL = 'package-payment-successful',

  SUBMIT_PLAN = 'submit-plan',

  ABORT = 'abort',
  DONE = 'done',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    id: 'steps-pricing-plan',

    initial: State.CREATION_PRESENTATION,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.CREATION_PRESENTATION]: {
        on: {
          ABORT: State.ABORT,
          NEXT: State.PLAN_SELECTION,
          PREV: State.ABORT,
        },
      },

      [State.PLAN_SELECTION]: {
        entry: [
          pure((context) => {
            const { isMobile } = context.getModuleData();
            return shared.actions.trackView({
              properties: {
                isMobile,
              },
            });
          }),
        ],

        on: {
          ABORT: State.ABORT,
          PREV: State.CREATION_PRESENTATION,

          SAVE_SELECTED_PACKAGE: {
            actions: Action.AssignSelectedPackage,
            target: State.PACKAGE_RECAP,
          },
        },
      },

      [State.PACKAGE_RECAP]: {
        on: {
          ABORT: State.ABORT,
          NEXT: State.TERMS_AND_CONDITIONS,
          PREV: State.PLAN_SELECTION,
        },
      },

      [State.TERMS_AND_CONDITIONS]: {
        always: {
          cond: Guard.ShouldSkipInsurance,
          target: State.SUBMIT_PLAN,
        },
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.SUBMIT_PLAN,
          PREV: [
            {
              cond: Guard.HasSelectedPackage,
              target: State.PACKAGE_RECAP,
            },
            { target: State.PLAN_SELECTION },
          ],
        },
      },

      [State.SUBMIT_PLAN]: {
        invoke: {
          onDone: [
            {
              cond: Guard.HasSelectedPackage,
              target: State.PACKAGE_PAYMENT,
            },
            { target: State.DONE },
          ],

          onError: {
            actions: shared.actions.error(),
            target: State.TERMS_AND_CONDITIONS,
          },

          src: Service.RegisterPricingPlanSelection,
        },
      },

      [State.PACKAGE_PAYMENT]: {
        on: {
          PAY: State.PACKAGE_PAYMENT_SEND,
          PREV: State.PACKAGE_RECAP,
        },
      },

      [State.PACKAGE_PAYMENT_SEND]: {
        invoke: {
          onDone: State.DONE,
          onError: {
            actions: shared.actions.error(),
            target: State.PACKAGE_PAYMENT,
          },

          src: Service.PayPackage,
        },
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },
    },
  },
  {
    actions,
    guards,
    services,
  },
);

export default machine;
